import { Button } from '@repo/ui/button'
import { DataProvider } from '@repo/ui/dataProvider'
import { useT } from '@repo/ui/hooks'
import { ScrollArea } from '@repo/ui/scrollArea'
import { UsersIcon } from 'lucide-react'
import { ReactNode } from 'react'

import { BeneficiaryCreatorTrigger } from '@/components/beneficiary/BeneficiaryCreator'
import { useBeneficiaryListAsSheet } from '@/components/beneficiary/list/sheet/hook'
import { DashboardSmartBlocks } from '@/components/dashboard/DashboardSmartBlocks'
import { Tasks } from '@/components/dashboard/Tasks'
import { WidgetList } from '@/components/dashboard/widgets/WidgetList'
import { WidgetsProvider } from '@/components/dashboard/widgets/WidgetsProvider'
import { WidgetsSelector } from '@/components/dashboard/widgets/WidgetsSelector'
import { SkeletonCard } from '@/components/skeletons/SkeletonCard'
import { RootState } from '@/store'
import { defaultFilterTemplate } from '@/store/beneficiary-list-filters/slice'

export function Dashboard() {
  return (
    <div className="mt-4 flex flex-1 flex-col gap-4 xl:gap-6 2xl:mt-6 overflow-hidden">
      <WidgetsProvider>
        <Header>
          <BeneficiaryCreatorTrigger variant="outline" className="w-fit" />
          <AllBeneficiariesButton />
          <WidgetsSelector />
        </Header>
        <ScrollArea className="overflow-y-auto px-4">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2 mb-4">
            <DataProvider
              selectors={[(state: RootState) => state.beneficiary.all]}
              render={(beneficiaries) => <DashboardSmartBlocks beneficiaries={beneficiaries} />}
            />
            <WidgetList />
          </div>
          <Tasks />
        </ScrollArea>
      </WidgetsProvider>
    </div>
  )
}

export function AllBeneficiariesButton() {
  const { t } = useT({ keyPrefix: 'dashboard.beneficiaryList' })
  const { setOpen } = useBeneficiaryListAsSheet()
  const handleOpen = () => setOpen(true, { filterTemplate: defaultFilterTemplate })
  return (
    <Button variant="outline" onClick={handleOpen}>
      <UsersIcon className="size-4" />
      {t('cta')}
    </Button>
  )
}

function Header({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2 px-4">
      <h1 className="text-primary-600 flex select-none gap-2 px-2 pt-1 text-xl font-semibold sm:text-2xl xl:text-3xl">
        <Greeting />
        <DataProvider
          loadingComponent={<SkeletonCard />}
          selectors={[(state: RootState) => state.user.caseWorker]}
          render={(caseWorker) => <span className="text-primary-500">{caseWorker.firstName}</span>}
        />
      </h1>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  )
}

function Greeting() {
  const { tc } = useT({ keyPrefix: 'dashboard' })
  const currentHour = new Date().getHours()
  const greeting =
    currentHour < 12
      ? tc('goodMorning')
      : currentHour < 18
        ? tc('goodAfternoon')
        : tc('goodEvening')
  return <>{greeting},</>
}
